import { Button } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import {
  FilterAccordion,
  getInitialExpanded,
} from "../../utils/util-components/UtilComponents";
import Flex from "../Flex";
import GlobalFilter from "../GlobalFilter";
import { ScrollArea } from "../ScrollBar";
import { FilterRecord } from "./filter-constant";
import { getComponent, type DateRangePresetValue } from "./filter-util";
import { isInvalidDate } from "@/utils/date-time-utility";

interface FilterBuilderProps<T> {
  filters: FilterRecord;
  onApply: (v: T) => void;
  prefillValues?: T;
  activeFilterCount?: number;
  h?: string;
}

/**
 * @param filters - The filters to display in the filter builder.
 * @param onApply - The function to call when the filter is applied.
 * @param prefillValues - The values to prefill in the filter builder.
 *
 * @returns A filter builder component.
 *
 * @example [filters for FilterBuilder]
 * const filters = {
 *   name: {
 *     type: "select",
 *     placeholder: "Name",
 *     options: [{ label: "John", value: "john" }],
 *   },
 *  "custom_age":{
 *    type:"custom",
 *    component: <CustomComponent />,
 *   }
 * };
 *
 * @example
 * <FilterBuilder
 *   filters={filters}
 *   onApply={onApply}
 *   prefillValues={prefillValues}
 *   activeFilterCount={activeFilterCount}
 * />
 *
 * @author [Rajkumar Rathod]
 */
export const FilterBuilder = <T,>({
  filters,
  onApply,
  prefillValues,
  activeFilterCount,
  h = "20.625rem",
}: FilterBuilderProps<T>) => {
  const initialValues = Object.keys(filters).reduce((acc, key) => {
    const f = filters[key];
    if (f?.type === "date_range_preset" && f?.childKeys) {
      f.childKeys.forEach(
        (ck) => (acc[ck] = (prefillValues as any)?.[ck] ?? undefined)
      );
    } else {
      acc[key] = (prefillValues as any)?.[key] ?? undefined;
    }
    return acc;
  }, {} as any);

  const clearedValues = Object.keys(filters).reduce((acc, key) => {
    const f = filters[key];
    if (f?.type === "date_range_preset" && f?.childKeys) {
      f.childKeys.forEach((ck) => (acc[ck] = undefined));
    } else {
      acc[key] = undefined;
    }
    return acc;
  }, {} as any);

  const formik = useFormik({
    initialValues: prefillValues || initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values as T),
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeFilter = () => setAnchorEl(null);

  const hasInvalidDate = Object.values(filters).some((f) =>
    f?.childKeys?.some((ck) =>
      isInvalidDate((formik.values as Record<string, unknown>)[ck])
    )
  );

  const submitAndClose = () => {
    if (hasInvalidDate) return;
    formik.submitForm();
    closeFilter();
  };

  const onChange = (key: string, value: any) => {
    formik.setFieldValue(key, value);
  };

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }>(
    getInitialExpanded(Object.keys(formik.values))
  );

  function handleExpandClick(key: string) {
    const copiedData = { ...expanded };
    copiedData[key] = !copiedData[key];
    setExpanded(copiedData);
  }

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        flexDirection={"column"}
        width={"17.438rem"}
        minHeight={h}
        maxHeight={"20.625rem"}
      >
        <Flex width={"100%"} justifyContent={"end"} alignItems={"center"}>
          <Button
            sx={{ height: "34px" }}
            variant="text"
            onClick={() => {
              formik.resetForm({ values: clearedValues });
              formik.submitForm();
            }}
          >
            Clear Filter
          </Button>
        </Flex>
        <ScrollArea height={"80%"}>
          {Object.entries(filters).map(([k, value]) => {
            const isPreset =
              value?.type === "date_range_preset" && value?.childKeys;
            const fieldValue = isPreset
              ? (value.childKeys as string[]).reduce(
                  (o, ck) => ({ ...o, [ck]: (formik.values as any)[ck] }),
                  {}
                )
              : (formik.values as any)[k];
            const fieldOnChange = isPreset
              ? (v: string | DateRangePresetValue | unknown[] | undefined) => {
                  if (
                    v &&
                    typeof v === "object" &&
                    !Array.isArray(v) &&
                    "date_range" in v
                  ) {
                    Object.entries(v).forEach(([key, val]) =>
                      onChange(key, val)
                    );
                  } else {
                    onChange(k, v);
                  }
                }
              : (v: unknown) => onChange(k, v);
            return (
              <FilterAccordion
                key={k}
                k={k}
                label={value?.label}
                expanded={expanded[k]}
                onExpand={handleExpandClick}
              >
                {getComponent(
                  fieldValue ?? undefined,
                  fieldOnChange,
                  value.type,
                  value?.options,
                  value?.placeholder,
                  value?.onSearch,
                  k,
                  value?.maxDate,
                  value?.minDate
                )}
              </FilterAccordion>
            );
          })}
        </ScrollArea>
        <Flex width={"100%"} justifyContent={"end"} height={"34px"}>
          <Button
            variant="text"
            sx={{ color: "gray" }}
            onClick={() => {
              formik.resetForm();
              submitAndClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={submitAndClose}
            disabled={hasInvalidDate}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default FilterBuilder;
